import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { periodParams } from "@/lib/reportsApi";
import { REPORTS } from "@/constants/testIds";
import PeriodSelector from "@/components/reports/PeriodSelector";
import KpiCard from "@/components/reports/KpiCard";
import RevenueChart from "@/components/reports/RevenueChart";
import OccupancyBars from "@/components/reports/OccupancyBars";
import ClientsCard from "@/components/reports/ClientsCard";
import NoShowCard from "@/components/reports/NoShowCard";
import InventorySummaryCard from "@/components/reports/InventorySummaryCard";
import { MostSoldTable, MostProfitableTable } from "@/components/reports/ServicesRankingTables";
import FollowUpKpiCard from "@/components/followups/FollowUpKpiCard";

/** Dashboard ejecutivo del Admin: consolida ingresos, servicios, ocupación,
 * clientes, no-shows e inventario, todo calculado en vivo desde los datos
 * ya registrados en Agenda/Pagos/Inventario/Fichas. */
export default function AdminDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState({ period: "month", start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (period.period === "custom" && (!period.start || !period.end)) return;
    setLoading(true);
    api.get("/reports/dashboard", { params: periodParams(period) })
      .then(({ data }) => setData(data))
      .catch((err) => toast({ title: "No se pudo cargar el dashboard", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-8" data-testid={REPORTS.adminDashboard}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-foreground">
            Hola, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-base text-muted-foreground mt-2">Este es el resumen ejecutivo de tu clínica.</p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Ingresos de hoy" amount={data?.revenue_kpis.today ?? 0} range={data?.revenue_kpi_ranges?.today} testId={REPORTS.kpiToday} />
        <KpiCard label="Ingresos de la semana" amount={data?.revenue_kpis.week ?? 0} range={data?.revenue_kpi_ranges?.week} testId={REPORTS.kpiWeek} />
        <KpiCard label="Ingresos del mes" amount={data?.revenue_kpis.month ?? 0} range={data?.revenue_kpi_ranges?.month} testId={REPORTS.kpiMonth} />
        <KpiCard label="Ingresos del año" amount={data?.revenue_kpis.year ?? 0} range={data?.revenue_kpi_ranges?.year} testId={REPORTS.kpiYear} />
      </div>

      <FollowUpKpiCard />

      {loading || !data ? (
        <p className="text-center text-muted-foreground py-12">Cargando dashboard...</p>
      ) : (
        <>
          <RevenueChart data={data.revenue_series} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <MostSoldTable data={data.top_services} />
            <MostProfitableTable data={data.top_profitable_services} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white border border-border rounded-xl p-5 lg:col-span-1">
              <h3 className="text-sm font-medium text-foreground mb-4">Ocupación por profesional</h3>
              <OccupancyBars data={data.occupancy_by_professional} />
            </div>
            <ClientsCard newCount={data.clients.new} returningCount={data.clients.returning} />
            <NoShowCard noShow={data.no_show} />
          </div>

          <InventorySummaryCard summary={data.inventory_summary} lowStockProducts={data.low_stock_products} />
        </>
      )}
    </div>
  );
}
